import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Search, Building2, Phone, Mail, ArrowRight, CheckCircle, Shield, Sparkles } from 'lucide-react';
import NatureImageCarousel from '../../components/NatureImageCarousel';

export default function ContactManagerLanding() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <Users className="w-6 h-6 text-blue-500" />
              <span className="text-xl font-bold gradient-text">Contact Manager</span>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/contactmanager/signup" className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600">
                Start Free
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-blue-50 to-white">
        {/* Nature Image Carousel */}
        <NatureImageCarousel />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mt-16">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              All Your Business Contacts in One Place
              <span className="block text-3xl md:text-4xl mt-4 mb-4">
                The Simple Way to Manage Business Contacts
              </span>
              <span className="block text-blue-500 text-2xl md:text-3xl mt-4">
                No complexity. Just clean, organized contacts.
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Stop struggling with contacts scattered across various sites, apps, and platforms.
              Let Gekko Contact Manager unify them all in one easy-to-manage place - organized, accessible, and up-to-date.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
              <Link 
                to="/contactmanager/signup"
                className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-3 rounded-full hover:shadow-lg transition-all flex items-center justify-center space-x-2"
              >
                <Sparkles className="w-5 h-5" />
                <span>Get Started Free</span>
              </Link>
              <a 
                href="#features"
                className="border-2 border-blue-500 text-blue-600 px-8 py-3 rounded-full hover:bg-blue-50 transition-all flex items-center justify-center space-x-2"
              >
                <Search className="w-5 h-5" />
                <span>See How It Works</span>
              </a>
            </div>
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            {[
              {
                icon: <Building2 className="w-8 h-8 text-blue-500" />,
                title: "Business & Personal Contacts",
                description: "Keep track of clients, vendors, partners, and team members all in one place."
              },
              {
                icon: <Shield className="w-8 h-8 text-blue-500" />,
                title: "Simple Yet Powerful",
                description: "All the features you need, none of the bloat. Perfect for small businesses."
              },
              {
                icon: <CheckCircle className="w-8 h-8 text-blue-500" />,
                title: "Always Up to Date",
                description: "Never lose track of important contact details or relationship history again."
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white/80 backdrop-blur-sm p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <div className="flex flex-col items-center text-center">
                  {feature.icon}
                  <h3 className="mt-4 font-semibold text-gray-900">{feature.title}</h3>
                  <p className="mt-2 text-gray-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Everything You Need, Nothing You Don't</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Designed specifically for small businesses and teams who need a better way to manage contacts.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: <Users className="w-6 h-6 text-blue-500" />,
                title: "Contact Organization",
                description: "Organize contacts by type, tags, or custom categories. Find anyone instantly."
              },
              {
                icon: <Building2 className="w-6 h-6 text-blue-500" />,
                title: "Company Profiles",
                description: "Link contacts to their organizations. Track company relationships effortlessly."
              },
              {
                icon: <Phone className="w-6 h-6 text-blue-500" />,
                title: "Multiple Contact Points",
                description: "Store multiple phone numbers, email addresses, and social profiles per contact."
              },
              {
                icon: <Mail className="w-6 h-6 text-blue-500" />,
                title: "Quick Actions",
                description: "Call, email, or message contacts directly from their profile with one click."
              },
              {
                icon: <Search className="w-6 h-6 text-blue-500" />,
                title: "Smart Search",
                description: "Find any contact instantly with powerful search and filtering options."
              },
              {
                icon: <Shield className="w-6 h-6 text-blue-500" />,
                title: "Data Security",
                description: "Your contact data is encrypted and secure. You're always in control."
              }
            ].map((feature, index) => (
              <div key={index} className="p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100">
                <div className="flex items-start space-x-4">
                  <div className="bg-white p-2 rounded-lg shadow-sm">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Ready to Get Organized?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of businesses who've simplified their contact management.
          </p>
          <Link 
            to="/contactmanager/signup"
            className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-3 rounded-full hover:shadow-lg transition-all flex items-center justify-center space-x-2"
          >
            <span>Get Started Free</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="mt-4 text-sm text-blue-100">
            Contact Manager is free!
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <Users className="w-6 h-6 text-blue-500" />
              <span className="text-xl font-bold gradient-text">Contact Manager v0.5.3</span>
            </div>
            <div className="flex items-center space-x-6">
              <a href="#features" className="text-gray-600 hover:text-gray-800">
                Features
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}